import React, { useState, useEffect } from "react";
import "./App.css";
import { TESTIMONIALS } from "./data/testimonials";
import { WELLNESS_CATEGORIES } from "./data/categories";
import { PRODUCTS } from "./data/products";

function TextFlipper() {
  const messages = [
    "Stacking wellness magic...",
    "Personalizing your path to wellness...",
    "Balancing your body and brain...",
    "Customizing your supplement stack...",
    "Optimizing your health journey...",
    "Mixing herbs and science...",
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return <>{messages[index]}</>;
}

function App({ products }) {
  const h1Class = "h1 tw-mt-0 tw-text-7xl";
  const h2Class = "h2 tw-mt-0 tw-text-6xl";
  const h3Class = "h3 tw-mt-0 tw-text-4xl";
  const [superCategory, setSuperCategory] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(1);
  const [aiRecommendations, setAiRecommendations] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const apiUrl =
    process.env.NODE_ENV === "development"
      ? "/api/quiz"
      : `${process.env.REACT_APP_API_BASE_URL}/api/quiz`;

  const totalSteps = 7;
  const progressPercent = step === 1 ? 1 : (step / totalSteps) * 100;

  const gridColsClass =
    filteredProducts.length >= 4
      ? "tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4"
      : filteredProducts.length === 3
      ? "tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3"
      : filteredProducts.length === 2
      ? "tw-grid-cols-1 sm:tw-grid-cols-2"
      : "tw-grid-cols-1";

  const fetchRecommendations = async (answers) => {
    try {
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      const data = await res.json();
      return data.recommendations;
    } catch (error) {
      console.error("GPT fetch error:", error);
      return "There was a problem fetching recommendations.";
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  return (
    <div className="inner tw-relative tw-text-black">
      <div
        className={`tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-50 ${
          step > 1 && step !== 5 && !loading && step !== 7
            ? "tw-bg-white/50"
            : ""
        }`}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
          <div className="tw-min-w-[60px]">
            &nbsp;
            {step > 1 && step < 7 && !loading && (
              <button
                onClick={() => {
                  if (step === 5) {
                    setSelectedSubcategories([]);
                  }
                  setStep(step - 1);
                }}
                className="hover:tw-text-black"
              >
                ← Back
              </button>
            )}
          </div>
          <div className="tw-min-w-[100px]">
            &nbsp;
            {step > 2 && !loading && (
              <button
                onClick={() => {
                  setAnswers({
                    userType: null,
                    audience: null,
                    goal: null,
                    subcategories: [],
                  });
                  setSelectedSubcategories([]);
                  setStep(1);
                }}
                className="tw-text-gray-500 hover:tw-text-black tw-underline"
              >
                Restart Quiz
              </button>
            )}
          </div>
        </div>
        <div className="tw-px-4">
          <div className="tw-w-full tw-h-2 tw-rounded-full tw-bg-gray-100 backdrop-blur-xs">
            <div
              className="tw-h-2 tw-min-h-[5px] tw-bg-[#ffdc61] tw-rounded-full"
              style={{ width: `${progressPercent}%` }}
            >
              &nbsp;
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div
          className="tw-relative tw-min-h-screen-minus-header tw-bg-cover tw-bg-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4"
          style={{
            backgroundImage:
              "url('https://usercontent.formsort.com/mindbloom/a243cbef-a840-48b9-b928-5714a48b8300.jpg')",
          }}
        >
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-p-10 tw-max-w-xl">
            <svg
              className="tw-animate-spin tw-h-10 tw-w-10 tw-text-[#ffdc61]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="tw-opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="tw-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <div className="tw-text-white tw-drop-shadow tw-h-6">
              <TextFlipper />
            </div>
          </div>
        </div>
      )}

      {!loading && step === 1 && (
        <div
          className="tw-relative tw-min-h-screen-minus-header tw-bg-cover tw-bg-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4"
          style={{
            backgroundImage:
              "url('https://usercontent.formsort.com/mindbloom/a243cbef-a840-48b9-b928-5714a48b8300.jpg')",
          }}
        >
          <div className="tw-p-10 tw-max-w-3xl">
            <h1 className={`${h1Class} tw-mb-5`}>
              Welcome to Your Wellness Stack
            </h1>
            <p className="tw-text-gray-700 tw-mb-8">
              Take this free, 1-minute personalized assessment to learn what
              supplement stack best supports your wellness goals.
            </p>
            <button
              onClick={() => setStep(2)}
              className="tw-bg-black tw-text-white tw-py-3 tw-px-8 tw-rounded-full hover:tw-bg-gray-800 tw-transition"
            >
              Start now
            </button>
            <p className="tw-text-gray-600 tw-mt-4 tw-text-base tw-mx-auto">
              By clicking "Start now", you acknowledge you've read and
              <br /> accepted our{" "}
              <a href="/privacy" className="tw-underline">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="/terms" className="tw-underline">
                Terms of Use
              </a>
              .
            </p>
          </div>
        </div>
      )}

      {!loading && step === 2 && (
        <div className="step-2 step tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4 tw-p-10">
          <div className="tw-max-w-5xl tw-mx-auto tw-w-full">
            <h2 className={`${h2Class} tw-max-w-xl tw-mx-auto tw-mb-8`}>
              Which health focus fits you best?
            </h2>
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-gap-6 tw-w-full tw-max-w-4xl tw-mx-auto">
              <div
                onClick={() => {
                  setAnswers({ ...answers, audience: "Women's Health" });
                  setStep(3);
                }}
                className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-cursor-pointer tw-group"
              >
                <img
                  src="https://cdn.prod.website-files.com/63a1c97b43b15d4c89e1c2c4/66ba91586d386c228c11f326_Overcome%20Anxiety.avif"
                  alt="Women's Health"
                  className="tw-w-full tw-h-full tw-object-cover tw-transition-transform group-hover:tw-scale-105"
                />
                <div className="tw-absolute tw-inset-0 tw-bg-black/30 tw-flex tw-items-end tw-p-6">
                  <div className="tw-text-left tw-text-[#ffdc61] sm:tw-text-2xl">
                    Female-focused care
                  </div>
                </div>
              </div>
              <div
                onClick={() => {
                  setAnswers({ ...answers, audience: "Men's Health" });
                  setStep(3);
                }}
                className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-cursor-pointer tw-group"
              >
                <img
                  src="https://cdn.prod.website-files.com/63a1c97b43b15d4c89e1c2c4/66ba91588ae5ad6954ff4c75_Relieve%20Depression.avif"
                  alt="Men's Health"
                  className="tw-w-full tw-h-full tw-object-cover tw-transition-transform group-hover:tw-scale-105"
                />
                <div className="tw-absolute tw-inset-0 tw-bg-black/30 tw-flex tw-items-end tw-p-6">
                  <div className="tw-text-left tw-text-[#ffdc61] sm:tw-text-2xl">
                    Male-focused care
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && step === 3 && (
        <div className="step-3 step tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4 tw-p-10">
          <div className="tw-max-w-[1200px] tw-mx-auto tw-w-full">
            <h2 className={`${h2Class} tw-max-w-4xl tw-mx-auto tw-mb-8`}>
              What area of wellness are you interested in?
            </h2>
            <div className="tw-mx-auto tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-2 sm:tw-gap-6 tw-w-full tw-justify-center tw-place-items-center">
              {[
                ...new Set(WELLNESS_CATEGORIES.map((c) => c.superCategory)),
              ].map((superCat) => (
                <div
                  key={superCat}
                  onClick={() => {
                    setSuperCategory(superCat);
                    setAnswers({ ...answers, superCategory: superCat });
                    setStep(4);
                  }}
                  className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-cursor-pointer tw-group tw-aspect-square tw-w-full"
                >
                  <img
                    alt={superCat}
                    src={
                      WELLNESS_CATEGORIES.find(
                        (c) => c.superCategory === superCat
                      )?.superImage ||
                      "https://cdn.prod.website-files.com/63a1c97b43b15d4c89e1c2c4/66ba91586d386c228c11f326_Overcome%20Anxiety.avif"
                    }
                    className="tw-w-full tw-h-full tw-object-cover tw-transition-transform group-hover:tw-scale-105"
                  />
                  <div className="tw-absolute tw-inset-0 tw-bg-black/10 tw-flex tw-items-end tw-p-6 tw-bg-black/50">
                    <div className="tw-text-left tw-text-[#ffdc61] sm:tw-text-2xl">
                      {superCat}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading &&
        step === 4 &&
        (() => {
          const matchingSuperCat = WELLNESS_CATEGORIES.find(
            (sc) => sc.superCategory === superCategory
          );
          const matchingCats = matchingSuperCat?.categories || [];

          return (
            <div className="step-4 step tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4">
              <div
                className={`tw-mx-auto tw-w-full ${
                  matchingCats.length >= 4
                    ? "tw-max-w-[1200px]"
                    : matchingCats.length === 3
                    ? "tw-max-w-[1000px]"
                    : matchingCats.length === 2
                    ? "tw-max-w-3xl"
                    : "tw-max-w-2xl"
                }`}
              >
                <h2 className={`${h2Class} tw-max-w-xl tw-mx-auto tw-mb-8`}>
                  Which wellness goal fits you best?
                </h2>
                <div
                  className={`tw-mx-auto tw-grid tw-gap-2 sm:tw-gap-6 tw-w-full tw-justify-center tw-place-items-center ${
                    matchingCats.length >= 4
                      ? "tw-grid-cols-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4"
                      : matchingCats.length === 3
                      ? "tw-grid-cols-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3"
                      : matchingCats.length === 2
                      ? "tw-grid-cols-2 sm:tw-grid-cols-2"
                      : "tw-grid-cols-2"
                  }`}
                >
                  {matchingCats.length === 0 ? (
                    <p className="tw-text-gray-500">
                      No wellness goals found for this category.
                    </p>
                  ) : (
                    matchingCats.map((cats) => (
                      <div
                        key={cats.category}
                        onClick={() => {
                          setAnswers({ ...answers, goal: cats.category });
                          setSelectedSubcategories([]);
                          setStep(5);
                        }}
                        className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-cursor-pointer tw-group tw-aspect-square tw-w-full"
                      >
                        <img
                          alt={cats.category}
                          src={
                            cats.categoryImage ||
                            "https://cdn.prod.website-files.com/63a1c97b43b15d4c89e1c2c4/66ba91586d386c228c11f326_Overcome%20Anxiety.avif"
                          }
                          className="tw-w-full tw-h-full tw-object-cover tw-transition-transform group-hover:tw-scale-105"
                        />
                        <div className="tw-absolute tw-inset-0 tw-bg-black/10 tw-flex tw-items-end tw-p-6 tw-bg-black/50">
                          <div className="tw-text-left tw-text-[#ffdc61] sm:tw-text-2xl">
                            {cats.category}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {!loading && step === 5 && (
        <div
          className="step-5 tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4 tw-bg-cover tw-bg-center"
          style={{
            backgroundImage:
              "url('https://usercontent.formsort.com/mindbloom/ab2ed37d-3bde-4974-9aad-ee5a24619c06.jpg')",
          }}
        >
          <div className="tw-p-6 tw-max-w-2xl">
            <h2 className={`${h2Class} tw-mb-5`}>We can help with that!</h2>
            <p className="tw-text-gray-700 tw-mb-6">
              We believe in science-backed supplements that actually work. Dr.
              Hyman's personalized quiz uses your answers to find the right
              stack for your body and brain.
            </p>

            {(() => {
              const baseTestimonial =
                TESTIMONIALS[answers.goal] || TESTIMONIALS["Sleep"];
              const isWomen = answers.audience?.includes("Women");
              const isMen = answers.audience?.includes("Men");

              const t = {
                ...baseTestimonial,
                quote: isWomen
                  ? baseTestimonial.femaleQuote
                  : isMen
                  ? baseTestimonial.maleQuote
                  : baseTestimonial.quote,
                image: isWomen
                  ? baseTestimonial.femaleImage
                  : isMen
                  ? baseTestimonial.maleImage
                  : baseTestimonial.image,
                name: isWomen
                  ? baseTestimonial.femaleName
                  : isMen
                  ? baseTestimonial.maleName
                  : baseTestimonial.image,
              };
              return (
                <>
                  <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover"
                    />
                    <div className="tw-text-left">
                      <p className="tw-font-bold">{t.name}</p>
                      <p className="tw-font-bold">GOAL: {t.goal}</p>
                    </div>
                  </div>
                  <blockquote className="tw-border-t tw-border-l-0 tw-border-black tw-pt-4 tw-text-left tw-m-0 tw-pl-0 tw-text-gray-800">
                    “{t.quote}”
                  </blockquote>
                </>
              );
            })()}
            <button
              onClick={() => setStep(6)}
              className="tw-mt-6 tw-bg-black tw-text-white tw-py-3 tw-px-8 tw-rounded-full hover:tw-bg-gray-800 tw-transition"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {!loading && step === 6 && (
        <div className="step-6 step tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4">
          <div className="tw-max-w-xl tw-mx-auto tw-w-full">
            <h2 className={`${h2Class} tw-mb-8 tw-mt-4`}>
              Refine your <br />
              wellness goals
            </h2>
            <div className="tw-flex tw-gap-2 tw-flex-col">
              {(() => {
                const matchingSuperCategory = WELLNESS_CATEGORIES.find(
                  (sc) =>
                    sc.superCategory === superCategory &&
                    sc.categories.some((cat) => cat.category === answers.goal)
                );

                const selectedCategory = matchingSuperCategory?.categories.find(
                  (cat) => cat.category === answers.goal
                );

                if (!selectedCategory) {
                  return (
                    <p className="tw-text-gray-500">
                      No subcategories found for this goal.
                    </p>
                  );
                }

                return selectedCategory.subcategories
                  .filter((subcat) => {
                    const hiddenFor = subcat?.hiddenFor || [];
                    const audience = answers.audience?.toLowerCase();
                    if (!audience) return true;
                    if (audience.includes("women"))
                      return !hiddenFor.includes("women");
                    if (audience.includes("men"))
                      return !hiddenFor.includes("men");
                    return true;
                  })
                  .map((subcat) => {
                    const name =
                      typeof subcat === "string" ? subcat : subcat.name;
                    const description =
                      typeof subcat === "string"
                        ? ""
                        : subcat.description || "";

                    return (
                      <button
                        key={name}
                        onClick={() => {
                          setSelectedSubcategories((prev) =>
                            prev.includes(name)
                              ? prev.filter((sub) => sub !== name)
                              : [...prev, name]
                          );
                        }}
                        className={`tw-py-4 tw-px-6 tw-border tw-rounded-lg tw-flex tw-justify-between tw-items-center tw-w-full ${
                          selectedSubcategories.includes(name)
                            ? "tw-bg-[#ffdc61] tw-text-black"
                            : "tw-bg-white tw-text-black hover:tw-bg-[#ffdc61] hover:tw-text-black"
                        } tw-border-gray-300`}
                      >
                        <span
                          className={`tw-inline-block tw-w-5 tw-h-5 tw-rounded tw-border tw-mr-4 ${
                            selectedSubcategories.includes(name)
                              ? "tw-bg-black"
                              : "tw-bg-white"
                          }`}
                        ></span>
                        <span className="tw-w-full tw-block tw-text-left">
                          <div className="">{name}</div>
                          {/*description && (
                            <div className="tw-text-gray-600">
                              {description}
                            </div>
                          )*/}
                        </span>
                      </button>
                    );
                  });
              })()}
            </div>
            <button
              className={`tw-mt-6 tw-py-4 tw-px-8 tw-border tw-rounded-full ${
                selectedSubcategories.length === 0
                  ? "tw-bg-gray-300 tw-text-gray-600 tw-cursor-not-allowed"
                  : "tw-bg-black tw-text-white hover:tw-bg-gray-800"
              }`}
              onClick={async () => {
                if (selectedSubcategories.length === 0) return;
                const updatedAnswers = {
                  ...answers,
                  subcategories: selectedSubcategories,
                };
                setAnswers(updatedAnswers);
                console.log("🧪 Sending to GPT:", {
                  answers: updatedAnswers,
                  products,
                });

                setLoading(true);
                const startTime = Date.now();

                const result = await fetchRecommendations(
                  updatedAnswers,
                  products
                );

                const elapsed = Date.now() - startTime;
                const minDelay = 3000;

                setTimeout(() => {
                  setAiRecommendations(result);
                  setLoading(false);
                  setStep(7);
                }, Math.max(0, minDelay - elapsed));

                setAiRecommendations(result);
                setStep(7);
              }}
              disabled={selectedSubcategories.length === 0}
            >
              Get My Stack
            </button>
          </div>
        </div>
      )}

      {!loading && step === 7 && (
        <div className="step-7 step tw-min-h-screen-minus-header tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-pt-20">
          <div className="tw-max-w-[1100px] tw-mx-auto tw-w-full">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-12 tw-items-center tw-mt-16 tw-mb-8 tw-justify-between">
              <h2 className={`${h2Class}`}>
                Your stack match for {answers.goal}
              </h2>
              <button className="tw-hidden tw-min-w-[200px] sm:tw-block tw-bg-[#ffdc61] tw-text-black tw-py-4 tw-px-6 tw-rounded-full hover:tw-bg-gray-800 hover:tw-text-white tw-transition">
                {" "}
                Add all to cart
              </button>
            </div>
            <div
              className={`tw-grid ${gridColsClass} tw-gap-6 ${
                filteredProducts.length < 4 ? "tw-justify-center" : ""
              }`}
            >
              {aiRecommendations?.stacks?.length === 0 && (
                <p>No recommendations found. Please try again.</p>
              )}
            </div>
            {aiRecommendations && (
              <div className="tw-mt-6 tw-mx-auto tw-w-full tw-mb-8 tw-flex tw-flex-col tw-gap-12 tw-items-start">
                <div className="recommendations tw-w-full">
                  <p className="tw-text-gray-600 tw-mb-2">
                    Based on your responses, we believe this personalized
                    supplement stack will help you reach your wellness goals.
                  </p>
                  <p className="tw-text-gray-600">
                    Functional medicine expert and bestselling author Dr. Mark
                    Hyman has spent decades helping people rebalance their
                    bodies with targeted, science-backed nutrition. This
                    customized plan draws on his proven approach to support your
                    unique health needs.
                  </p>

                  {aiRecommendations.supportAreas?.length > 0 && (
                    <div className="tw-pt-6 tw-flex tw-flex-col sm:tw-flex-row tw-gap-8 sm:tw-items-center">
                      <h4 className="">Focus Areas</h4>
                      <ul className="tw-list-none tw-flex tw-flex-wrap tw-gap-2">
                        {aiRecommendations.supportAreas.map((area) => (
                          <li
                            className="tw-py-4 tw-px-8 tw-bg-gray-100 tw-text-black tw-rounded-full"
                            key={area}
                          >
                            {area}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="recommendations tw-w-full">
                  <ul
                    className={`tw-mx-auto tw-grid tw-gap-2 sm:tw-gap-4 tw-w-full tw-justify-center tw-grid-cols-1 sm:tw-grid-cols-3`}
                  >
                    {aiRecommendations.products?.map((product) => {
                      return (
                        <li key={product.name} className="tw-h-full">
                          <div className="tw-border tw-rounded-2xl tw-h-full tw-p-8 tw-m-0 tw-flex tw-flex-col tw-items-start">
                            {products.find((p) => p.title === product.name)
                              ?.image && (
                              <div
                                className="tw-overflow-hidden tw-rounded-2xl tw-w-full tw-flex tw-items-center tw-justify-center tw-aspect-square tw-relative"
                                style={{ backgroundColor: "#ffdc61" }}
                              >
                                <img
                                  src={
                                    products.find(
                                      (p) => p.title === product.name
                                    ).image
                                  }
                                  alt={product.name}
                                  className="tw-w-full tw-object-contain tw-max-w-[70%] tw-max-h-[70%] tw-mx-auto"
                                />
                              </div>
                            )}
                            <div className="info tw-w-full tw-flex tw-flex-col tw-justify-between">
                              <div className="top">
                                <p className="tw-text-gray-400 tw-mt-4">
                                  Category: {product.category}
                                </p>
                                <p className="tw-text-4xl tw-mb-2">
                                  {product.name}
                                </p>
                                <p className="tw-mt-2">
                                  <b>What It Does:</b> {product.whatItDoes}
                                </p>
                                <p className="tw-mt-3">
                                  <b>Why It Matters:</b> {product.whyItMatters}
                                </p>
                              </div>
                              <div className="bottom">
                                <p className="tw-text-gray-400 tw-mt-2">
                                  Dosage: {product.dosage}
                                </p>
                                {product.references &&
                                  product.references[0]?.title && (
                                    <p className="tw-text-gray-400 tw-mt-2">
                                      References:{" "}
                                      <a
                                        target="_blank"
                                        className="tw-border-b tw-border-gray-400 hover:tw-border-b-2 tw-transition"
                                        href={product.references[0].url}
                                      >
                                        {product.references[0].title} ↗
                                      </a>
                                    </p>
                                  )}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {aiRecommendations.extras?.lifestyleTips?.length > 0 && (
                    <div className="tw-mt-4 tw-pt-12">
                      <h4 className="">Lifestyle Tips</h4>
                      <ul className="tw-list-disc tw-list-inside">
                        {aiRecommendations.extras.lifestyleTips.map((tip) => (
                          <li key={tip}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {aiRecommendations.extras?.warnings?.length > 0 && (
                    <div className="tw-mt-4 tw-border-t tw-border-gray-200 tw-pt-8 tw-pb-20">
                      <h4 className="">Safety</h4>
                      <ul className="tw-list-disc tw-list-inside">
                        {aiRecommendations.extras.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default () => <App products={PRODUCTS} />;
